import { dispatchNative, setNativeProperty } from "./nativeDom";
import {
  applyValidityClass,
  reconcileLengthConstraint,
} from "../validation/validityStyling";

// Mirrors what a real submission would collect (multiple entries for the
// same name - checkboxes - become an array) so restoring is a matter of
// writing the same values back into the same named controls.
export const serializeFormValues = (form) => {
  const data = {};
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value !== "string") continue; // skip any file inputs
    if (key in data) {
      data[key] = Array.isArray(data[key])
        ? [...data[key], value]
        : [data[key], value];
    } else {
      data[key] = value;
    }
  }

  // buildDateQuestion only ever writes the hidden combined field once the
  // date is fully valid, so FormData never sees an in-progress or invalid
  // date at all. Capture the raw day/month/year/time text too, so typing
  // "30" into the day box isn't silently lost the moment the page refreshes.
  form.querySelectorAll('[data-date-part="value"]').forEach((hidden) => {
    const container = hidden.closest(".date-input-group");
    if (!container || !hidden.name) return;

    const raw = {};
    container.querySelectorAll("[data-date-part]").forEach((el) => {
      const part = el.getAttribute("data-date-part");
      if (part !== "value" && el.value) raw[part] = el.value;
    });
    if (Object.keys(raw).length) {
      data[`${hidden.name}__rawDate`] = JSON.stringify(raw);
    }
  });

  return data;
};

// DateQuestion's visible day/month/year/time boxes carry no name - only the
// hidden combined field does - so restoring it means decomposing the
// persisted "YYYY-MM-DDTHH:MM"-shaped string back into whichever of those
// boxes are actually rendered, mirroring how buildDateQuestion joined them.
const restoreDateGroup = (hiddenInput, value) => {
  if (!value) return;
  const container = hiddenInput.closest(".date-input-group");
  if (!container) return;

  const day = container.querySelector('[data-date-part="day"]');
  const month = container.querySelector('[data-date-part="month"]');
  const year = container.querySelector('[data-date-part="year"]');
  const time = container.querySelector('[data-date-part="time"]');

  const [datePart, timePart] = value.split("T");
  const segments = datePart.split("-");
  if (year) setNativeProperty(year, "value", segments.shift() ?? "");
  setNativeProperty(month, "value", segments.shift() ?? "");
  if (day) setNativeProperty(day, "value", segments.shift() ?? "");
  if (time && timePart) setNativeProperty(time, "value", timePart);

  [day, month, year, time]
    .filter(Boolean)
    .forEach((el) => dispatchNative(el, "input"));
};

// Restores the raw day/month/year/time text captured alongside the hidden
// combined field (see serializeFormValues) - the only way an in-progress or
// invalid date's digits survive a refresh, since restoreDateGroup above
// only has something to decompose once a date was fully valid. Only fills
// in parts still blank, so it never fights with a value restoreDateGroup
// already set from a valid hidden field.
const restoreRawDateParts = (hiddenInput, raw) => {
  const container = hiddenInput.closest(".date-input-group");
  if (!container) return;

  const touched = Object.entries(raw)
    .map(([part, partValue]) => {
      const el = container.querySelector(`[data-date-part="${part}"]`);
      if (!el || el.value) return null;
      setNativeProperty(el, "value", partValue);
      return el;
    })
    .filter(Boolean);

  touched.forEach((el) => dispatchNative(el, "input"));
};

const RAW_DATE_SUFFIX = "__rawDate";

export const restoreFormValues = (form, data) => {
  Object.entries(data).forEach(([name, value]) => {
    if (name.endsWith(RAW_DATE_SUFFIX)) {
      const hidden = form.elements.namedItem(
        name.slice(0, -RAW_DATE_SUFFIX.length),
      );
      if (hidden) restoreRawDateParts(hidden, JSON.parse(value));
      return;
    }

    const namedItem = form.elements.namedItem(name);
    if (!namedItem) return;

    const elements =
      typeof namedItem.length === "number"
        ? Array.from(namedItem)
        : [namedItem];
    const first = elements[0];

    if (first.hasAttribute("data-date-part")) {
      restoreDateGroup(first, Array.isArray(value) ? value[0] : value);
      return;
    }

    if (first.type === "checkbox" || first.type === "radio") {
      const values = Array.isArray(value) ? value : [value];
      elements.forEach((el) => {
        const shouldCheck = values.includes(el.value);
        if (el.checked !== shouldCheck) {
          // React listens for the native "click" event to detect a
          // checkbox/radio change (a historical browser-compat quirk in
          // its event system), not "change" - a dispatched Event("change")
          // is silently ignored, so onChange (and therefore any
          // setCustomValidity it performs) never runs. Calling the real
          // .click() method fires the exact sequence a genuine click does
          // - including toggling .checked and unchecking radio siblings -
          // so it's picked up correctly. (On a fresh render every option
          // starts unchecked, so this only ever fires for the box that
          // needs to become checked.)
          el.click();
        }
        // MultiChoiceCheckQuestion's own onChange already restyles every
        // box in the group (including ones that stayed unchecked) as part
        // of handling the .click() above - applying the generic styling
        // on top would clear that for any box this loop didn't click.
        if (!el.hasAttribute("data-group-validated")) {
          applyValidityClass(el);
        }
      });
      return;
    }

    const singleValue = Array.isArray(value) ? value[0] : value;
    if (first.value !== singleValue) {
      setNativeProperty(first, "value", singleValue);
      dispatchNative(first, first.tagName === "SELECT" ? "change" : "input");
    }
    reconcileLengthConstraint(first);
    applyValidityClass(first);
  });
};
